import { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Divider,
    Box,
    Stack,
    Grid,
    Select,
    Option,
    Sheet,
    Alert,
    IconButton,
    Checkbox,
    Button,
} from '@mui/joy';
import {
    getMandatsWithUnassignedCsv,
    assignCollaborateurToCsv,
} from '@app/api/data/potentiels-csv-file';
import { getCollaborateurs, UserLogin } from '@app/api/data/user-login';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import AppClubNameDisplay from '@app/components/AppClubNameDisplay';
import { AppIcon } from '@app/components/AppIcon';

const PreparationParrainagePotentiel = () => {
    const [mandats, setMandats] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [collaborateurs, setCollaborateurs] = useState<UserLogin[]>([]);
    const [assignLoading, setAssignLoading] = useState<number | null>(null);
    const [expandedMandats, setExpandedMandats] = useState<Set<number>>(
        new Set()
    );
    const [archivedStates, setArchivedStates] = useState<Map<number, boolean>>(
        new Map()
    );
    const [mandatCollaborateur, setMandatCollaborateur] = useState<
        Map<number, number>
    >(new Map());
    const [mandatArchived, setMandatArchived] = useState<Map<number, boolean>>(
        new Map()
    );
    const [mandatAssignLoading, setMandatAssignLoading] = useState<
        Set<number>
    >(new Set());

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [mandatsRes, collaborateursRes] = await Promise.all([
                    getMandatsWithUnassignedCsv(),
                    getCollaborateurs(),
                ]);
                setMandats(mandatsRes.data);
                setCollaborateurs(
                    Array.isArray(collaborateursRes.result)
                        ? collaborateursRes.result
                        : []
                );
            } catch (e: any) {
                setError(
                    e?.response?.data?.message ||
                        e?.message ||
                        'Erreur lors du chargement'
                );
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleAssign = async (
        csvId: number,
        userLoginId: number,
        archived?: boolean
    ) => {
        setAssignLoading(csvId);
        try {
            const res = await assignCollaborateurToCsv(
                csvId,
                userLoginId,
                archived
            );
            // Afficher le nombre d'entrées créées
            if (res.data.entriesCreated) {
                alert(`${res.data.entriesCreated} entrées créées avec succès`);
            }
            // Réinitialiser l'état archivé pour ce CSV
            setArchivedStates((prev) => {
                const newMap = new Map(prev);
                newMap.delete(csvId);
                return newMap;
            });
            setMandats((prev) =>
                prev
                    .map((mandat) => ({
                        ...mandat,
                        potentielsCsvFile: Array.isArray(
                            mandat.potentielsCsvFile
                        )
                            ? mandat.potentielsCsvFile.filter(
                                  (csv: any) => csv.id !== csvId
                              )
                            : [],
                    }))
                    .filter(
                        (mandat) =>
                            Array.isArray(mandat.potentielsCsvFile) &&
                            mandat.potentielsCsvFile.length > 0
                    )
            );
        } catch (e: any) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    "Erreur lors de l'assignation"
            );
        } finally {
            setAssignLoading(null);
        }
    };

    const toggleExpand = (mandatId: number) => {
        setExpandedMandats((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(mandatId)) {
                newSet.delete(mandatId);
            } else {
                newSet.add(mandatId);
            }
            return newSet;
        });
    };

    const handleAssignAllForMandat = async (mandatId: number) => {
        const collaborateurId = mandatCollaborateur.get(mandatId);
        const archived = mandatArchived.get(mandatId) || false;

        if (!collaborateurId) {
            alert('Veuillez sélectionner un collaborateur');
            return;
        }

        const mandat = mandats.find((m) => m.id === mandatId);
        if (!mandat || !Array.isArray(mandat.potentielsCsvFile)) {
            return;
        }

        const csvIds = mandat.potentielsCsvFile.map((csv: any) => csv.id);
        setMandatAssignLoading((prev) => {
            const newSet = new Set(prev);
            newSet.add(mandatId);
            return newSet;
        });

        try {
            let totalEntries = 0;
            for (const csvId of csvIds) {
                try {
                    const res = await assignCollaborateurToCsv(
                        csvId,
                        collaborateurId,
                        archived
                    );
                    if (res.data.entriesCreated) {
                        totalEntries += res.data.entriesCreated;
                    }
                } catch (e) {
                    console.error(
                        `Erreur lors de l'assignation du CSV ${csvId}:`,
                        e
                    );
                }
            }

            if (totalEntries > 0) {
                alert(
                    `${totalEntries} entrées créées avec succès pour ${csvIds.length} CSV(s)`
                );
            }

            // Réinitialiser les états
            setMandatCollaborateur((prev) => {
                const newMap = new Map(prev);
                newMap.delete(mandatId);
                return newMap;
            });
            setMandatArchived((prev) => {
                const newMap = new Map(prev);
                newMap.delete(mandatId);
                return newMap;
            });

            // Rafraîchir la liste
            setMandats((prev) =>
                prev
                    .map((m) => ({
                        ...m,
                        potentielsCsvFile: Array.isArray(m.potentielsCsvFile)
                            ? m.potentielsCsvFile.filter(
                                  (csv: any) => !csvIds.includes(csv.id)
                              )
                            : [],
                    }))
                    .filter(
                        (m) =>
                            Array.isArray(m.potentielsCsvFile) &&
                            m.potentielsCsvFile.length > 0
                    )
            );
        } catch (e: any) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    "Erreur lors de l'assignation groupée"
            );
        } finally {
            setMandatAssignLoading((prev) => {
                const newSet = new Set(prev);
                newSet.delete(mandatId);
                return newSet;
            });
        }
    };

    return (
        <Box className="mt-8 p-2">
            <div className="flex items-center justify-start gap-2">
                <AppIcon icon="Square3Stack3DIcon" />

                <Typography className="py-4" level="h4">
                    Parrainages Potentiels non affectés
                </Typography>
            </div>
            {loading ? (
                <Sheet className="h-40vh flex items-center justify-center">
                    <Typography>Chargement...</Typography>
                </Sheet>
            ) : error ? (
                <Alert color="danger">{error}</Alert>
            ) : (
                <Stack spacing={3}>
                    {Array.isArray(mandats) && mandats.length === 0 && (
                        <Typography>Aucun mandat à traiter</Typography>
                    )}
                    {Array.isArray(mandats) &&
                        mandats.map((mandat) => {
                            const nbCsv = Array.isArray(
                                mandat.potentielsCsvFile
                            )
                                ? mandat.potentielsCsvFile.length
                                : 0;
                            return (
                                <Card key={mandat.id} variant="outlined">
                                    <Box
                                        className="flex cursor-pointer items-center justify-between"
                                        onClick={() => toggleExpand(mandat.id)}
                                    >
                                        <Box className="flex items-center gap-2">
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                            >
                                                {expandedMandats.has(
                                                    mandat.id
                                                ) ? (
                                                    <KeyboardArrowDownIcon />
                                                ) : (
                                                    <KeyboardArrowRightIcon />
                                                )}
                                            </IconButton>
                                            <Typography level="title-md">
                                                Mandat : {mandat.ref}
                                            </Typography>
                                            <AppClubNameDisplay
                                                mandatRef={mandat.ref}
                                                showRef={false}
                                                color="neutral"
                                                level="body-sm"
                                            />
                                        </Box>
                                        <Typography
                                            color="primary"
                                            level="body-md"
                                        >
                                            {nbCsv} CSV non affecté
                                            {nbCsv > 1 ? 's' : ''}
                                        </Typography>
                                    </Box>

                                    {expandedMandats.has(mandat.id) && (
                                        <>
                                            <Divider className="my-1" />
                                            <Stack spacing={2}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        // justifyContent: 'flex-end',
                                                        gap: 2,
                                                        alignItems: 'center',
                                                        p: 1,
                                                        bgcolor: 'background.level1',
                                                        borderRadius: 'sm',
                                                    }}
                                                >
                                                    <Grid spacing={2} className='w-full m-2 space-x-4 flex items-center justify-end'>
                                                        <Grid xs={5}>
                                                            <Select
                                                                placeholder="Assigner un collaborateur à tous"
                                                                value={
                                                                    mandatCollaborateur.get(
                                                                        mandat.id
                                                                    ) || null
                                                                }
                                                                onChange={(_, value) => {
                                                                    if (value) {
                                                                        setMandatCollaborateur(
                                                                            (
                                                                                prev
                                                                            ) => {
                                                                                const newMap =
                                                                                    new Map(
                                                                                        prev
                                                                                    );
                                                                                newMap.set(
                                                                                    mandat.id,
                                                                                    value as number
                                                                                );
                                                                                return newMap;
                                                                            }
                                                                        );
                                                                    }
                                                                }}
                                                                disabled={
                                                                    mandatAssignLoading.has(
                                                                        mandat.id
                                                                    )
                                                                }
                                                            >
                                                                {Array.isArray(
                                                                    collaborateurs
                                                                ) &&
                                                                    collaborateurs.map(
                                                                        (
                                                                            collab
                                                                        ) => (
                                                                            <Option
                                                                                key={
                                                                                    collab.id
                                                                                }
                                                                                value={
                                                                                    collab.id
                                                                                }
                                                                            >
                                                                                {
                                                                                    collab.userName
                                                                                }
                                                                            </Option>
                                                                        )
                                                                    )}
                                                            </Select>
                                                        </Grid>
                                                        <Grid xs={4}>
                                                            <Checkbox
                                                                checked={
                                                                    mandatArchived.get(
                                                                        mandat.id
                                                                    ) ||
                                                                    false
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) => {
                                                                    setMandatArchived(
                                                                        (
                                                                            prev
                                                                        ) => {
                                                                            const newMap =
                                                                                new Map(
                                                                                    prev
                                                                                );
                                                                            newMap.set(
                                                                                mandat.id,
                                                                                event
                                                                                    .target
                                                                                    .checked
                                                                            );
                                                                            return newMap;
                                                                        }
                                                                    );
                                                                }}
                                                                disabled={
                                                                    mandatAssignLoading.has(
                                                                        mandat.id
                                                                    )
                                                                }
                                                                label={
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'center',
                                                                            gap: 1,
                                                                        }}
                                                                    >
                                                                        <AppIcon
                                                                            icon="ArchiveBoxIcon"
                                                                        />
                                                                        <Typography>
                                                                            Créer avec le
                                                                            statut archivé
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid xs={3}>
                                                            <Button
                                                                onClick={() =>
                                                                    handleAssignAllForMandat(
                                                                        mandat.id
                                                                    )
                                                                }
                                                                disabled={
                                                                    !mandatCollaborateur.get(
                                                                        mandat.id
                                                                    ) ||
                                                                    mandatAssignLoading.has(
                                                                        mandat.id
                                                                    )
                                                                }
                                                                loading={
                                                                    mandatAssignLoading.has(
                                                                        mandat.id
                                                                    )
                                                                }
                                                                size="sm"
                                                                color="primary"
                                                            >
                                                                Appliquer à tous
                                                            </Button>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                                <Divider />
                                                <Stack spacing={1}>
                                                {Array.isArray(
                                                    mandat.potentielsCsvFile
                                                ) &&
                                                    [
                                                        ...mandat.potentielsCsvFile,
                                                    ]
                                                        .sort((a, b) =>
                                                            a.filename.localeCompare(
                                                                b.filename,
                                                                'fr',
                                                                {
                                                                    sensitivity:
                                                                        'base',
                                                                    numeric:
                                                                        true,
                                                                }
                                                            )
                                                        )
                                                        .map((csv: any) => (
                                                            <Grid
                                                                container
                                                                key={csv.id}
                                                                alignItems="center"
                                                                spacing={2}
                                                            >
                                                                <Grid xs={4}>
                                                                    <Typography>
                                                                        {
                                                                            csv.filename
                                                                        }
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid xs={5}>
                                                                    <Select
                                                                        placeholder="Assigner un collaborateur"
                                                                        onChange={(
                                                                            _,
                                                                            value
                                                                        ) => {
                                                                            if (value) {
                                                                                const archived =
                                                                                    archivedStates.get(
                                                                                        csv.id
                                                                                    ) ||
                                                                                    false;
                                                                                handleAssign(
                                                                                    csv.id,
                                                                                    value as number,
                                                                                    archived
                                                                                );
                                                                            }
                                                                        }}
                                                                        disabled={
                                                                            assignLoading ===
                                                                            csv.id
                                                                        }
                                                                    >
                                                                        {Array.isArray(
                                                                            collaborateurs
                                                                        ) &&
                                                                            collaborateurs.map(
                                                                                (
                                                                                    collab
                                                                                ) => (
                                                                                    <Option
                                                                                        key={
                                                                                            collab.id
                                                                                        }
                                                                                        value={
                                                                                            collab.id
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            collab.userName
                                                                                        }
                                                                                    </Option>
                                                                                )
                                                                            )}
                                                                    </Select>
                                                                </Grid>
                                                                <Grid xs={3}>
                                                                    <Checkbox
                                                                        checked={
                                                                            archivedStates.get(
                                                                                csv.id
                                                                            ) ||
                                                                            false
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) => {
                                                                            setArchivedStates(
                                                                                (
                                                                                    prev
                                                                                ) => {
                                                                                    const newMap =
                                                                                        new Map(
                                                                                            prev
                                                                                        );
                                                                                    newMap.set(
                                                                                        csv.id,
                                                                                        event
                                                                                            .target
                                                                                            .checked
                                                                                    );
                                                                                    return newMap;
                                                                                }
                                                                            );
                                                                        }}
                                                                        disabled={
                                                                            assignLoading ===
                                                                            csv.id
                                                                        }
                                                                        label={
                                                                            <Box
                                                                                sx={{
                                                                                    display:
                                                                                        'flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    gap: 1,
                                                                                }}
                                                                            >
                                                                                <AppIcon
                                                                                    icon="ArchiveBoxIcon"
                                                                                />
                                                                                <Typography>
                                                                                    Créer avec le
                                                                                    statut archivé
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        ))}
                                                </Stack>
                                            </Stack>
                                        </>
                                    )}
                                </Card>
                            );
                        })}
                </Stack>
            )}
        </Box>
    );
};

export default PreparationParrainagePotentiel;
